import FileServices from 'services/FileServices';
import DeviceInfoServices from 'services/DeviceInfoServices';
import { consumerIdSelector } from 'store/profile/selectors';
import { apiKeySelector } from 'store/appSettings/selectors';
import { dfdAccessTokenSelector, fhirCurrentUserPatientId } from 'store/authentication/selectors';
import Config from 'react-native-config';
import { RootDispatch, RootState, Thunk } from 'store/types';
import { navigateToPage } from 'store/navigation/actions';
import { Screens } from 'store/messaging/constants';
import { Action } from 'modules/types/common';

const PREFIX = 'BILLING';
export const DETAIL_DOWNLOAD = `${PREFIX}.DETAIL_DOWNLOAD`;
export const DETAIL_DOWNLOAD_SUCCESS = `${PREFIX}.DETAIL_DOWNLOAD_SUCCESS`;
export const DETAIL_DOWNLOAD_FAIL = `${PREFIX}.DETAIL_DOWNLOAD_FAIL`;
export const DETAIL_SHARE = `${PREFIX}.DETAIL_SHARE`;
export const DETAIL_SHARE_SUCCESS = `${PREFIX}.DETAIL_SHARE_SUCCESS`;
export const DETAIL_SHARE_FAIL = `${PREFIX}.DETAIL_SHARE_FAIL`;
export const GET_PAYMENT_DETAIL = `${PREFIX}.GET_PAYMENT_DETAIL`;
export const GET_ENCOUNTER_DETAIL = `${PREFIX}.GET_ENCOUNTER_DETAIL`;

export const downloadBillingDetail = (encounterId: string) => async (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  dispatch({ type: DETAIL_DOWNLOAD });
  try {
    const currentState = getState();
    const patientId = fhirCurrentUserPatientId(currentState);
    const accessToken = dfdAccessTokenSelector(currentState);
    const apikey = apiKeySelector(currentState);
    const consumerId = consumerIdSelector(currentState);
    const deviceId = DeviceInfoServices.getDeviceId();
    const response = await FileServices.download(
      `billing-detail_${encounterId}.pdf`,
      `${Config.BILLING_BASE_URL}/rest/system/encounters/${encounterId}/download`,
      {
        Accept: 'application/pdf',
        Authorization: `Bearer ${accessToken}`,
        apikey,
        'x-device-id': deviceId,
        'x-dfd-consumer-id': consumerId,
        'x-patient-id': patientId
      }
    );

    return dispatch({ type: DETAIL_DOWNLOAD_SUCCESS, payload: response });
  } catch (err) {
    return dispatch({ type: DETAIL_DOWNLOAD_FAIL, Error: err });
  }
};

export const downloadBillingDetailToStore = (encounterId: string, asPdf?: boolean): Action => ({
  type: DETAIL_DOWNLOAD,
  payload: {
    client: 'billing',
    request: {
      method: 'get',
      url: `/rest/system/encounters/${encounterId}/download`,
      headers: {
        Accept: asPdf ? 'application/pdf' : 'application/json'
      }
    }
  }
});

export const shareBillingDetail = (
  payload: { id: string },
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
): Thunk => (dispatch: RootDispatch) => {
  return dispatch({
    type: DETAIL_SHARE,
    payload: {
      client: 'billing',
      request: {
        method: 'post',
        url: `/rest/system/encounters/${payload.id}/share`,
        data: {
          toAddress,
          message,
          useSecureEmail,
          contactPhoneNumber,
          subject
        }
      }
    }
  });
};

export const getPaymentDetail = (receiptId: any) => (dispatch: RootDispatch) => {
  return dispatch({
    type: GET_PAYMENT_DETAIL,
    payload: {
      client: 'billing',
      request: {
        method: 'get',
        url: `/rest/system/payments/${receiptId}`
      }
    }
  });
};
export const openShareBillingDetail = (id: string): Thunk => (dispatch: RootDispatch) => {
  const params = {
    subject: `Share Billing detail for encounter ${id}`,
    transmitParams: {
      returnBackTo: 'BillingDetail',
      source: 'BillingDetail',
      payload: { id }
    }
  };

  dispatch(navigateToPage({ routeName: Screens.MESSAGES_COMPOSE, params }));
};

export const PAYMENT_DOWNLOAD = `${PREFIX}.PAYMENT_DOWNLOAD`;
export const PAYMENT_DOWNLOAD_SUCCESS = `${PREFIX}.PAYMENT_DOWNLOAD_SUCCESS`;
export const PAYMENT_DOWNLOAD_FAIL = `${PREFIX}.PAYMENT_DOWNLOAD_FAIL`;
export const PAYMENT_SHARE = `${PREFIX}.PAYMENT_SHARE`;
export const PAYMENT_SHARE_SUCCESS = `${PREFIX}.PAYMENT_SHARE_SUCCESS`;
export const PAYMENT_SHARE_FAIL = `${PREFIX}.PAYMENT_SHARE_FAIL`;

export const downloadPaymentDetail = (receiptId: string) => async (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  dispatch({ type: PAYMENT_DOWNLOAD });
  try {
    const currentState = getState();
    const patientId = fhirCurrentUserPatientId(currentState);
    const accessToken = dfdAccessTokenSelector(currentState);
    const apikey = apiKeySelector(currentState);
    const consumerId = consumerIdSelector(currentState);
    const deviceId = DeviceInfoServices.getDeviceId();

    const response = await FileServices.download(
      `payment-detail_${receiptId.replace(/[^a-zA-Z0-9]/g, '')}.pdf`,
      `${Config.BILLING_BASE_URL}/rest/system/payments/${receiptId}/download`,
      {
        Accept: 'application/pdf',
        Authorization: `Bearer ${accessToken}`,
        apikey,
        'x-device-id': deviceId,
        'x-dfd-consumer-id': consumerId,
        'x-patient-id': patientId
      }
    );

    return dispatch({ type: PAYMENT_DOWNLOAD_SUCCESS, payload: response });
  } catch (err) {
    return dispatch({ type: PAYMENT_DOWNLOAD_FAIL, error: err });
  }
};

export const downloadPaymentDetailToStore = (receiptId: string, asPdf?: boolean) => ({
  type: PAYMENT_DOWNLOAD,
  payload: {
    client: 'billing',
    request: {
      method: 'get',
      url: `/rest/system/payments/${receiptId}/download`,
      headers: {
        Accept: asPdf ? 'application/pdf' : 'application/json'
      }
    }
  }
});

export const sharePaymentDetail = (
  payload: { id: string },
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
): Action => ({
  type: PAYMENT_SHARE,
  payload: {
    client: 'billing',
    request: {
      method: 'post',
      url: `/rest/system/payments/${payload.id}/share`,
      data: {
        toAddress,
        message,
        useSecureEmail,
        contactPhoneNumber,
        subject
      }
    }
  }
});

export const openSharePaymentDetail = (id: string): Thunk => (dispatch: RootDispatch) => {
  const params = {
    subject: `Share payment detail for receipt ${id}`,
    transmitParams: {
      returnBackTo: 'PaymentDetail',
      source: 'PaymentDetail',
      payload: { id }
    }
  };

  dispatch(navigateToPage({ routeName: Screens.MESSAGES_COMPOSE, params }));
};
export const getEncounterDetail = (encounterId: string) => (dispatch: RootDispatch) => {
  return dispatch({
    type: GET_ENCOUNTER_DETAIL,
    payload: {
      client: 'billing',
      request: {
        method: 'get',
        url: `/rest/system/encounters/${encounterId}`
      }
    }
  });
};
